import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import ErrorToast from './ErrorToast';

const AUTO_DISMISS_MS = 6000;
const EXIT_MS = 280;

interface ErrorToastPayload {
  title: string;
  description?: string;
}

const ErrorToastContext = createContext<(title: string, description?: string) => void>(() => {});

export const ErrorToastProvider = ({ children }: { children: ReactNode }) => {
  const [toast, setToast] = useState<ErrorToastPayload | null>(null);
  const [exiting, setExiting] = useState(false);
  const dismissTimerRef = useRef<number>();
  const exitTimerRef = useRef<number>();

  const dismiss = useCallback(() => {
    window.clearTimeout(dismissTimerRef.current);
    setExiting(true);
    exitTimerRef.current = window.setTimeout(() => {
      setToast(null);
      setExiting(false);
    }, EXIT_MS);
  }, []);

  const showError = useCallback(
    (title: string, description?: string) => {
      window.clearTimeout(dismissTimerRef.current);
      window.clearTimeout(exitTimerRef.current);
      setExiting(false);
      setToast({ title, description });
      dismissTimerRef.current = window.setTimeout(dismiss, AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  useEffect(
    () => () => {
      window.clearTimeout(dismissTimerRef.current);
      window.clearTimeout(exitTimerRef.current);
    },
    [],
  );

  return (
    <ErrorToastContext.Provider value={showError}>
      {children}
      {toast ? (
        <ErrorToast
          title={toast.title}
          description={toast.description}
          exiting={exiting}
          onClose={dismiss}
        />
      ) : null}
    </ErrorToastContext.Provider>
  );
};

export const useErrorToast = (): ((title: string, description?: string) => void) =>
  useContext(ErrorToastContext);
